import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  ApproveTimesheetsBatchInput,
  CopyPreviousWeekInput,
  CopyPreviousWeekResult,
  PaginatedResult,
  PreloadCurrentWeekResult,
  RejectTimesheetInput,
  RejectTimesheetsBatchInput,
  RemoveEmptyEntriesResult,
  SetTimesheetTimingInput,
  Timesheet,
  TimesheetBatchResult,
  TimesheetDueConfig,
  TimesheetStatus,
  TimesheetSummary,
  TimesheetTaskRowsResult,
  UpdateTimesheetDueConfigInput,
  UpdateTimesheetInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface TimesheetListParams {
  page: number;
  pageSize: number;
  memberId?: string;
  memberIds?: string[];
  approverId?: string;
  approverIds?: string[];
  status?: TimesheetStatus;
  weekStartDate?: string;
  weekStartFrom?: string;
  weekStartTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/** Filters for the aggregate summary (the whole filtered set, no paging). */
export type TimesheetSummaryParams = Omit<
  TimesheetListParams,
  'page' | 'pageSize' | 'sortBy' | 'sortDir'
>;

const timesheetKeys = {
  all: ['timesheets'] as const,
  list: (params: TimesheetListParams) => ['timesheets', 'list', params] as const,
  summary: (params: TimesheetSummaryParams) => ['timesheets', 'summary', params] as const,
  detail: (id: string) => ['timesheets', 'detail', id] as const,
};

export function useTimesheets(
  params: TimesheetListParams,
): UseQueryResult<PaginatedResult<Timesheet>> {
  return useQuery({
    queryKey: timesheetKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Timesheet>>(
        `/timesheets${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          memberId: params.memberId,
          memberIds: params.memberIds?.join(','),
          approverId: params.approverId,
          approverIds: params.approverIds?.join(','),
          status: params.status,
          weekStartDate: params.weekStartDate,
          weekStartFrom: params.weekStartFrom,
          weekStartTo: params.weekStartTo,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

/** Aggregate totals (hours, status counts, on-time vs late) for the filtered set. */
export function useTimesheetsSummary(
  params: TimesheetSummaryParams,
): UseQueryResult<TimesheetSummary> {
  return useQuery({
    queryKey: timesheetKeys.summary(params),
    queryFn: () =>
      api.get<TimesheetSummary>(
        `/timesheets/summary${buildQuery({
          memberId: params.memberId,
          memberIds: params.memberIds?.join(','),
          approverId: params.approverId,
          approverIds: params.approverIds?.join(','),
          status: params.status,
          weekStartDate: params.weekStartDate,
          weekStartFrom: params.weekStartFrom,
          weekStartTo: params.weekStartTo,
        })}`,
      ),
  });
}

export function useTimesheet(id: string): UseQueryResult<Timesheet> {
  return useQuery({
    queryKey: timesheetKeys.detail(id),
    queryFn: () => api.get<Timesheet>(`/timesheets/${id}`),
    enabled: Boolean(id),
  });
}

export function useSubmitTimesheet(): UseMutationResult<Timesheet, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Timesheet>(`/timesheets/${id}/submit`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
    },
  });
}

export function useApproveTimesheet(): UseMutationResult<Timesheet, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Timesheet>(`/timesheets/${id}/approve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
    },
  });
}

export interface RejectTimesheetArgs {
  id: string;
  input: RejectTimesheetInput;
}

export function useRejectTimesheet(): UseMutationResult<Timesheet, unknown, RejectTimesheetArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: RejectTimesheetArgs) =>
      api.post<Timesheet>(`/timesheets/${id}/reject`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
    },
  });
}

/** Admin-only: reopen an approved timesheet back to draft so it can be corrected/resubmitted. */
export function useReopenTimesheet(): UseMutationResult<Timesheet, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Timesheet>(`/timesheets/${id}/reopen`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

/** Admin-only: permanently delete a DRAFT timesheet and its never-submitted entries. */
export function useDeleteTimesheet(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/timesheets/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

export interface SetTimesheetTimingArgs {
  id: string;
  input: SetTimesheetTimingInput;
}

/** Admin-only: manually set (or clear) a timesheet's on-time vs past-due override. */
export function useSetTimesheetTiming(): UseMutationResult<
  Timesheet,
  unknown,
  SetTimesheetTimingArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SetTimesheetTimingArgs) =>
      api.patch<Timesheet>(`/timesheets/${id}/timing`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['reports', 'bonus'] });
    },
  });
}

export function useApproveTimesheetsBatch(): UseMutationResult<
  TimesheetBatchResult,
  unknown,
  ApproveTimesheetsBatchInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApproveTimesheetsBatchInput) =>
      api.post<TimesheetBatchResult>('/timesheets/approve-batch', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
    },
  });
}

export function useRejectTimesheetsBatch(): UseMutationResult<
  TimesheetBatchResult,
  unknown,
  RejectTimesheetsBatchInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RejectTimesheetsBatchInput) =>
      api.post<TimesheetBatchResult>('/timesheets/reject-batch', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
    },
  });
}

export function usePreloadCurrentWeek(): UseMutationResult<
  PreloadCurrentWeekResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<PreloadCurrentWeekResult>('/timesheets/preload-current-week'),
    onSuccess: (result) => {
      if (result.timesheet) {
        void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
      }
    },
  });
}

export interface UpdateTimesheetArgs {
  id: string;
  input: UpdateTimesheetInput;
}

export function useUpdateTimesheet(): UseMutationResult<Timesheet, unknown, UpdateTimesheetArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateTimesheetArgs) =>
      api.patch<Timesheet>(`/timesheets/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
    },
  });
}

export interface SetBonusDisqualificationArgs {
  id: string;
  disqualified: boolean;
  note?: string | null;
}

/** Approver-only: set/clear a timesheet's bonus-disqualified flag and note. */
export function useSetTimesheetBonusDisqualification(): UseMutationResult<
  Timesheet,
  unknown,
  SetBonusDisqualificationArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, disqualified, note }: SetBonusDisqualificationArgs) =>
      api.patch<Timesheet>(`/timesheets/${id}/bonus-disqualification`, { disqualified, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['reports', 'bonus'] });
    },
  });
}

export interface CopyPreviousWeekArgs {
  id: string;
  input: CopyPreviousWeekInput;
}

export function useCopyPreviousWeek(): UseMutationResult<
  CopyPreviousWeekResult,
  unknown,
  CopyPreviousWeekArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: CopyPreviousWeekArgs) =>
      api.post<CopyPreviousWeekResult>(`/timesheets/${id}/copy-previous-week`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

/**
 * Fetches the member's open tickets as unsaved "ghost" row descriptors for the grid.
 * Read-only — nothing is written until the user logs hours in a ghost cell. Modeled
 * as a mutation so the button can trigger it on demand with a loading state.
 */
export function useLoadOpenTicketRows(): UseMutationResult<
  TimesheetTaskRowsResult,
  unknown,
  string
> {
  return useMutation({
    mutationFn: (id: string) =>
      api.get<TimesheetTaskRowsResult>(`/timesheets/${id}/open-ticket-rows`),
  });
}

/** Same as `useLoadOpenTicketRows`, but for the tasks the member logged last week. */
export function useLoadPreviousWeekRows(): UseMutationResult<
  TimesheetTaskRowsResult,
  unknown,
  string
> {
  return useMutation({
    mutationFn: (id: string) =>
      api.get<TimesheetTaskRowsResult>(`/timesheets/${id}/previous-week-rows`),
  });
}

export function useRemoveEmptyEntries(): UseMutationResult<
  RemoveEmptyEntriesResult,
  unknown,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<RemoveEmptyEntriesResult>(`/timesheets/${id}/remove-empty`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
}

const dueConfigKey = ['timesheets', 'due-config'] as const;

export function useTimesheetDueConfig(): UseQueryResult<TimesheetDueConfig> {
  return useQuery({
    queryKey: dueConfigKey,
    queryFn: () => api.get<TimesheetDueConfig>('/timesheets/due-config'),
  });
}

export function useUpdateTimesheetDueConfig(): UseMutationResult<
  TimesheetDueConfig,
  unknown,
  UpdateTimesheetDueConfigInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTimesheetDueConfigInput) =>
      api.put<TimesheetDueConfig>('/timesheets/due-config', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dueConfigKey });
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all });
    },
  });
}
